package components

import androidx.compose.runtime.Composable
import calculator.CalculatorInput
import calculator.ScenarioId
import calculator.ScenarioResult
import calculator.defaultCalculationAssumptions
import calculator.inflationMultiplier
import format.formatNumber
import format.formatRub
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Ul

@Composable
fun FormulaPanel(input: CalculatorInput, scenarios: List<ScenarioResult>) {
    Div({ classes("formula-panel") }) {
        H2 { Text("Формула расчета") }
        Ul({ classes("formula-list") }) {
            Li { Text("Курс: 1 RUB = ${formatNumber(input.vndPerThousandRub / 1_000.0)} VND") }
            Li { Text("Доход за рабочий день = доход в месяц / рабочих дней в месяце = ${formatRub(input.monthlyIncomeRub / input.workDaysPerMonth)}") }
            Li { Text("Множитель инфляции = сумма коэффициентов по годам: 1 + 1.1 + 1.21 ... = ${formatNumber(inflationMultiplier(input.years, input.annualInflationPercent))}") }
            Li { Text("Потеря времени умножается на рост дохода и коэффициент стоимости рабочего дня: ${formatNumber(input.taxAndOpportunityCostFactor)}x") }
            Li { Text("Фактический период = срок пребывания - запас дней до выезда (${formatNumber(input.bufferDaysBeforeExit)} дней)") }
            Li { Text("Визаранов в год = дней в году / период пребывания") }
            Li { Text("Для штампа: первые ${formatNumber(input.noEVisaStampChangeAfterYears)} года считаются по ${formatNumber(input.noEVisaPeriodDays)} дням, затем по ${formatNumber(input.noEVisaChangedPeriodDays)} дням") }
            Li { Text("Расходы на поездки = визаранов в год * (поездка + отель/еда) в RUB * инфляция и изменение курса") }
            Li { Text("Потеря дохода = визаранов за период * рабочих дней на визаран * доход за рабочий день * коэффициент") }
            Li { Text("Риск = визаранов за период * вероятность риска * стоимость события") }
            Li { Text("Экономия АТЭС = fast-track + экономия на других странах АТЭС") }
            Li { Text("Итого = поездки + потеря дохода + администрирование + риск-дни + риск-события + дискомфорт + карта - экономии") }
        }
        Div({ classes("assumptions-list") }) {
            defaultCalculationAssumptions
                .filter { !it.key.startsWith("eVisa") }
                .forEach { assumption ->
                Div({ classes("assumption-item") }) {
                    Span({ classes("summary-label") }) { Text(assumption.label) }
                    P { Text("${assumptionValueDescription(input, assumption.key, assumption.valueDescription)}. Источник: ${assumption.sourceLabel}. Обновлено: ${assumption.lastUpdated}. Доверие: ${assumption.confidence}.") }
                }
            }
        }
        Div({ classes("formula-scroll") }) {
            Table({ classes("formula-table") }) {
                Thead {
                    Tr {
                        Th { Text("Вариант") }
                        Th { Text("Подстановка") }
                    }
                }
                Tbody {
                    scenarios.forEach { scenario ->
                        Tr {
                            Td { Text(scenario.title) }
                            Td {
                                Div({ classes("formula-expression") }) {
                                    Text(formulaText(input, scenario))
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun assumptionValueDescription(input: CalculatorInput, key: String, fallback: String): String {
    return when (key) {
        "noEVisaPeriodDays" -> "${formatNumber(input.noEVisaPeriodDays)} дней"
        "eVisaPeriodDays" -> "${formatNumber(input.eVisaPeriodDays)} дней"
        "apecPeriodDays" -> "${formatNumber(input.apecPeriodDays)} дней"
        "apecCardCostRub" -> formatRub(input.apecCardCostRub)
        else -> fallback
    }
}

private fun formulaText(input: CalculatorInput, scenario: ScenarioResult): String {
    val runCostRub = scenario.runCostVnd / (input.vndPerThousandRub / 1_000.0)
    val dailyIncomeRub = input.monthlyIncomeRub / input.workDaysPerMonth

    if (scenario.id == ScenarioId.NO_E_VISA && input.noEVisaStampChangeAfterYears < input.years) {
        val firstYears = input.noEVisaStampChangeAfterYears
        val secondYears = input.years - firstYears
        val firstRunsPerYear = input.daysPerYear / input.noEVisaPeriodDays
        val secondRunsPerYear = input.daysPerYear / input.noEVisaChangedPeriodDays

        return "поездки: (${formatNumber(firstRunsPerYear)} * ${formatNumber(firstYears)} года + " +
            "${formatNumber(secondRunsPerYear)} * ${formatNumber(secondYears)} года) * ${formatRub(runCostRub)} с инфляцией; " +
            "потеря дохода: ${formatNumber(scenario.runsForPeriod)} * ${formatNumber(scenario.lostWorkDaysPerRun)} * " +
            "${formatRub(dailyIncomeRub)}; итого = ${formatRub(scenario.totalRub)}"
    }

    return "(${formatNumber(input.daysPerYear)} / ${formatNumber(scenario.periodDays)}) * " +
        "${formatRub(runCostRub)} * ${formatNumber(inflationMultiplier(input.years, input.annualInflationPercent))} + " +
        "${formatNumber(scenario.runsForPeriod)} * ${formatNumber(scenario.lostWorkDaysPerRun)} * " +
        "${formatRub(dailyIncomeRub)} + скрытые расходы ${formatRub(hiddenCosts(scenario))} + карта ${formatRub(scenario.cardCostRub)} - " +
        "экономии ${formatRub(scenario.fastTrackSavingsRub + scenario.otherTravelSavingsRub)} = ${formatRub(scenario.totalRub)}"
}

private fun hiddenCosts(scenario: ScenarioResult): Double {
    return scenario.adminTimeCostRub + scenario.extraRiskDaysCostRub + scenario.expectedRiskCostRub + scenario.discomfortCostRub
}
